import { useState } from 'react';

import { api } from 'utils';

import './list-zero-workflow.scss';

/*
 * Lists the /list/submit test scenarios with a download for each CSV. The files
 * are anonymized and live under public/test-scenarios/.
 */

const BASE = process.env.PUBLIC_URL + '/test-scenarios';

type Scenario = { day: string; file: string; tests: string; expected: string };

const TIMELINE: Scenario[] = [
  { day: 'Day 1', file: 'day1_baseline.csv', tests: 'The starting list (200 members, 4 countries)', expected: 'Sets the baseline' },
  { day: 'Day 2', file: 'day2_additions.csv', tests: 'New joiners', expected: 'Clean joiner to List 2; one with a bad field to List 1; one with an unknown company to List 1' },
  { day: 'Day 3', file: 'day3_departures.csv', tests: 'Departures and a job-title change', expected: '2 leavers to List 2 (DE); 1 member dropped from the file to a DE; the job-title change creates no ticket' },
  { day: 'Day 4', file: 'day4_transfers_ec_ksa.csv', tests: 'AZADEAID change, transfer, Saudi Arabia', expected: 'AZADEAID change to an EC; transfer to a DE plus an AD; Saudi Arabia member creates no ticket' },
  { day: 'Day 5', file: 'day5_checking_zone.csv', tests: 'A new member who is already leaving', expected: 'Cancelled, no ticket' },
];

const STANDALONE: Scenario[] = [
  { day: 'Duplicate', file: 'standalone_duplicate_409.csv', tests: 'The same person twice in one file', expected: 'Rejected (409)' },
  { day: 'Too many missing', file: 'standalone_threshold_block.csv', tests: 'Most of the list missing at once', expected: 'Blocked, no tickets' },
];

type Outcome = 'ok' | 'note' | 'review';
type Member = { id: string; tests: string; result: string; outcome: Outcome };

const OUTCOME_LABEL: Record<Outcome, string> = {
  ok: 'Works',
  note: 'Note',
  review: 'Under review',
};

// Members that already exist in List 3, from members_already_in_list3.csv.
// The results below are the actual behaviour observed on the test data.
const MEMBERS_IN_LIST3: Member[] = [
  {
    id: 'EMP00125',
    tests: 'A member who is already insured is sent again as a new joiner.',
    result: 'No ticket. The system sees the member is already in List 3 and does not add them again.',
    outcome: 'ok',
  },
  {
    id: 'EMP00137',
    tests: 'An insured member is sent with a changed person ID, a blank gender, and a national ID that contains letters. The staff ID stays the same.',
    result: 'No ticket. The staff ID is unchanged and still insured, so the member is treated as existing. Changes to other fields do not create a ticket, so the blank gender and the bad national ID are not flagged.',
    outcome: 'note',
  },
  {
    id: 'PID00144',
    tests: 'An insured member is sent with a new staff ID (the AZADEA ID changes from EMP00144 to EMP01444).',
    result: 'The member is added again as a new joiner, so they would end up insured twice. The staff ID change is not recognised as a change. This case is under review.',
    outcome: 'review',
  },
  {
    id: 'EMP00147',
    tests: 'A member is terminated but no leave date is given.',
    result: 'A deletion is created in List 1 with an error, waiting for the leave date to be added before it can go to GLOBAL.',
    outcome: 'ok',
  },
  {
    id: 'EMP00155',
    tests: 'A member is terminated with a leave date in the future (15 August).',
    result: 'No ticket yet. The member is still covered until the leave date, so nothing is sent.',
    outcome: 'ok',
  },
  {
    id: 'EMP00156',
    tests: 'A member is terminated with a leave date that has already passed (29 July).',
    result: 'A deletion is created and moved to List 2, ready for GLOBAL (one ticket per policy).',
    outcome: 'ok',
  },
];

function ScenarioTable({ rows }: { rows: Scenario[] }) {
  return (
    <table className="workflow-table scenario-table">
      <thead>
        <tr>
          <th style={{ width: 140 }}>File</th>
          <th>What it tests</th>
          <th>Expected result</th>
          <th style={{ width: 120 }} />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.file}>
            <td>{r.day}</td>
            <td>{r.tests}</td>
            <td>{r.expected}</td>
            <td>
              <a className="scenario-download" href={`${BASE}/${r.file}`} download>
                Download
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ListZeroScenarios() {
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState('');

  const onReset = async () => {
    const ok = window.confirm(
      'This clears the List 0 test data (the participants baseline and all tickets) so you can run the scenarios again from the start. Continue?'
    );
    if (!ok) return;
    setResetting(true);
    setResetMsg('');
    try {
      const res = await api().post('/list/reset-scenarios');
      const cleared = (res && res.data && res.data.cleared) || {};
      const total = Object.keys(cleared).reduce((sum, k) => sum + (Number(cleared[k]) || 0), 0);
      setResetMsg(`Done. ${total} records cleared. Upload Day 1 to start again.`);
    } catch (e: any) {
      const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || '';
      setResetMsg(`Reset failed. ${msg}`);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="list-zero-workflow">

      <section className="workflow-card">
        <h2>Test Scenarios</h2>
        <p>
          Sample List 0 files for trying the flow. The data is anonymized. Upload the daily files in
          order and check List 1 and List 2 after each one.
        </p>
        <div className="scenario-reset-row">
          <button type="button" className="scenario-reset" onClick={onReset} disabled={resetting}>
            {resetting ? 'Resetting...' : 'Reset test data'}
          </button>
          {resetMsg && <span className="scenario-reset-msg">{resetMsg}</span>}
        </div>
        <ScenarioTable rows={TIMELINE} />
      </section>

      <section className="workflow-card">
        <h2>Single-file checks</h2>
        <p>Each of these is rejected on purpose. Upload them one at a time.</p>
        <ScenarioTable rows={STANDALONE} />
      </section>

      <section className="workflow-card">
        <h2>Members already in List 3</h2>
        <p>
          This example shows how the system handles employees who are already insured in List 3
          when List 0 is sent again with joiners, changes, and terminations. The data is anonymized.
          It is a reference example, not part of the daily timeline above.
        </p>
        <a className="scenario-download" href={`${BASE}/members_already_in_list3.csv`} download>
          Download the file
        </a>
        <table className="workflow-table scenario-table members-table">
          <thead>
            <tr>
              <th style={{ width: 130 }}>Member</th>
              <th>What it tests</th>
              <th>What the system does</th>
              <th style={{ width: 120 }}>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {MEMBERS_IN_LIST3.map((m) => (
              <tr key={m.id}>
                <td>{m.id}</td>
                <td>{m.tests}</td>
                <td>{m.result}</td>
                <td>
                  <span className={`scenario-outcome scenario-outcome--${m.outcome}`}>
                    {OUTCOME_LABEL[m.outcome]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="workflow-callout workflow-callout--danger">
          Staff ID change (PID00144): when a member who is already insured is sent with a new AZADEA
          ID, the change is not yet recognised and the member is added a second time. This case is
          under review. The other members in this file behave as expected.
        </div>
      </section>
    </div>
  );
}
